import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, MapPin, Phone, CheckCircle, Truck, Navigation, QrCode, ChevronRight, RefreshCcw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DeliveryProofCapture } from '@/components/admin/DeliveryProofCapture';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

const statusColors = {
  paid: 'bg-blue-100 text-blue-800',
  preparing: 'bg-orange-100 text-orange-800',
  ready_for_collection: 'bg-green-100 text-green-800',
  collected: 'bg-emerald-500 text-white',
};

export default function DriverDashboard() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [quickCode, setQuickCode] = useState('');
  const [quickLooking, setQuickLooking] = useState(false);
  const [quickOrder, setQuickOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [proof, setProof] = useState({ photo: null, signature: null });
  const [route, setRoute] = useState(null);
  const [showRoute, setShowRoute] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);

  const fetchRoute = async () => {
    setRouteLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/driver/route`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setRoute(await r.json());
    } catch (e) { /* silent */ }
    finally { setRouteLoading(false); }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const url = `${API_URL}/api/driver/orders${qs ? '?' + qs : ''}`;
      const r = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (r.ok) setOrders(await r.json());
    } catch (e) {
      console.error('Error fetching orders:', e);
    } finally {
      setLoading(false);
    }
  }, [token, search, statusFilter]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const openMaps = (address) => {
    const q = encodeURIComponent(address || 'GC3H Canteen, Midrand');
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
  };

  const lookupByCode = async () => {
    if (!quickCode.trim()) return;
    setQuickLooking(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/orders/lookup-by-code`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ collection_code: quickCode.toUpperCase() })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Not found');
      setQuickOrder(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setQuickLooking(false);
    }
  };

  const confirmDelivery = async (orderId, code) => {
    setVerifying(true);
    try {
      // Upload delivery proof first (if captured) — non-blocking on failure is avoided; surface errors
      if (proof.photo || proof.signature) {
        const pr = await fetch(`${API_URL}/api/driver/orders/${orderId}/delivery-proof`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ photo: proof.photo, signature: proof.signature })
        });
        if (!pr.ok) { const pd = await pr.json(); throw new Error(pd.detail || 'Could not save delivery proof'); }
      }
      const r = await fetch(`${API_URL}/api/admin/orders/${orderId}/verify-collection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ collection_code: code })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      toast.success('Delivery confirmed!');
      setSelectedOrder(null);
      setQuickOrder(null);
      setQuickCode('');
      setVerifyCode('');
      setProof({ photo: null, signature: null });
      fetchOrders();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="driver-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-600 rounded-xl flex items-center justify-center">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-lg">Deliveries</h1>
              <p className="text-xs text-gray-500">{orders.length} order{orders.length !== 1 ? 's' : ''} today</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { const n = !showRoute; setShowRoute(n); if (n && !route) fetchRoute(); }} data-testid="driver-route-toggle">
              <Navigation className="h-4 w-4 mr-1" /> Route
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchOrders(); }}>
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Today's Route */}
        {showRoute && (
          <div className="px-4 pb-3" data-testid="driver-route-panel">
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-indigo-800 flex items-center gap-2"><Navigation className="h-4 w-4" /> Today's Route</span>
                {route && <span className="text-xs text-indigo-600">{route.total_stops} stops · {route.total_orders} orders</span>}
              </div>
              {routeLoading ? (
                <p className="text-xs text-indigo-500 py-2">Loading route…</p>
              ) : !route || route.route.length === 0 ? (
                <p className="text-xs text-indigo-500 py-2">No deliveries scheduled for today.</p>
              ) : (
                <div className="space-y-2">
                  {route.route.map((stop) => (
                    <div key={stop.sequence} className="bg-white rounded-lg p-2.5 flex items-start gap-3" data-testid={`route-stop-${stop.sequence}`}>
                      <div className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{stop.sequence}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{stop.address}</p>
                        <p className="text-xs text-gray-500">{stop.orders.length} order{stop.orders.length !== 1 ? 's' : ''} · {stop.item_count} items</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Verify */}
        <div className="px-4 pb-3">
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-cyan-700" />
              <span className="text-sm font-semibold text-cyan-800">Quick Verify</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={quickCode}
                onChange={(e) => setQuickCode(e.target.value.toUpperCase())}
                placeholder="Collection code"
                className="font-mono tracking-wider flex-1 bg-white"
                maxLength={6}
                onKeyDown={(e) => e.key === 'Enter' && lookupByCode()}
                data-testid="driver-quick-code"
              />
              <Button onClick={lookupByCode} disabled={quickLooking} className="bg-cyan-600 hover:bg-cyan-700" data-testid="driver-lookup-btn">
                {quickLooking ? '...' : 'Find'}
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search orders, names, addresses..."
              className="pl-10"
              data-testid="driver-search"
            />
          </div>
        </div>

        {/* Status filters */}
        <div className="px-4 pb-3 flex gap-1.5 overflow-x-auto">
          {[
            { value: '', label: 'All Active' },
            { value: 'ready_for_collection', label: 'Ready' },
            { value: 'preparing', label: 'Preparing' },
            { value: 'paid', label: 'Paid' },
          ].map(f => (
            <button key={f.value} onClick={() => setStatusFilter(f.value)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                statusFilter === f.value ? 'bg-cyan-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      <div className="px-4 py-3 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-cyan-600 border-t-transparent rounded-full animate-spin" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No orders to deliver</p>
            <p className="text-gray-400 text-sm">Check back soon</p>
          </div>
        ) : orders.map((order) => (
          <motion.div
            key={order.order_id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer active:scale-[0.99] transition-transform"
            onClick={() => setSelectedOrder(order)}
            data-testid={`driver-order-${order.order_id}`}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className={statusColors[order.status] || 'bg-gray-100'}>{order.status?.replace(/_/g, ' ').toUpperCase()}</Badge>
                  {order.collection_code && <span className="font-mono text-sm font-bold text-cyan-700">{order.collection_code}</span>}
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
              <p className="font-semibold text-gray-900">{order.customer_name}</p>
              <p className="text-sm text-gray-500">{order.items?.length} item{order.items?.length !== 1 ? 's' : ''} &middot; R{order.total_amount?.toFixed(0)}</p>
              {(order.delivery_address_name || order.delivery_address) && (
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 flex-1 min-w-0">
                    <MapPin className="h-3.5 w-3.5 text-cyan-500 flex-shrink-0" />
                    <span className="truncate">{order.delivery_address_name || order.delivery_address || 'Pickup'}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openMaps(order.delivery_address || order.delivery_address_name); }}
                    className="ml-2 text-xs text-cyan-600 font-medium flex items-center gap-0.5 flex-shrink-0"
                  >
                    <Navigation className="h-3 w-3" /> Map
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => { if (!open) { setSelectedOrder(null); setVerifyCode(''); setProof({ photo: null, signature: null }); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order #{selectedOrder?.order_id?.slice(-8).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              {/* Customer */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-semibold text-gray-900">{selectedOrder.customer_name}</p>
                {selectedOrder.customer_phone && (
                  <a href={`tel:${selectedOrder.customer_phone}`} className="flex items-center gap-1.5 text-sm text-cyan-600 mt-1">
                    <Phone className="h-3.5 w-3.5" /> {selectedOrder.customer_phone}
                  </a>
                )}
              </div>

              {/* Delivery Address + Maps */}
              {selectedOrder.delivery_address_name && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Deliver to</p>
                      <p className="font-medium text-gray-900 text-sm">{selectedOrder.delivery_address_name}</p>
                      {selectedOrder.delivery_address && <p className="text-xs text-gray-500 mt-0.5">{selectedOrder.delivery_address}</p>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openMaps(selectedOrder.delivery_address || selectedOrder.delivery_address_name)}
                      className="text-cyan-600 border-cyan-200" data-testid="open-maps-btn">
                      <Navigation className="h-4 w-4 mr-1" /> Maps
                    </Button>
                  </div>
                </div>
              )}

              {/* Collection Code */}
              {selectedOrder.collection_code && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-cyan-600 font-medium">Collection Code</p>
                  <p className="font-mono text-2xl font-bold text-cyan-800 tracking-wider">{selectedOrder.collection_code}</p>
                </div>
              )}

              {/* Items (ALL including drinks) */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Order Items</p>
                <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center px-3 py-2.5">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name} x{item.quantity}</p>
                        {item.selected_sides?.length > 0 && (
                          <p className="text-xs text-green-700">+ {item.selected_sides.map(s => s.name).join(', ')}</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-700">R{(item.price * item.quantity).toFixed(0)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between px-3 py-2 mt-1 font-bold text-gray-900">
                  <span>Total</span>
                  <span>R{selectedOrder.total_amount?.toFixed(0)}</span>
                </div>
              </div>

              {/* Verify & Complete */}
              {selectedOrder.status === 'ready_for_collection' && (
                <div className="space-y-3">
                  <DeliveryProofCapture onChange={setProof} />
                  <Input
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                    placeholder="Enter collection code to confirm"
                    className="font-mono tracking-wider text-center text-lg"
                    maxLength={6}
                    data-testid="driver-verify-input"
                  />
                  <Button
                    onClick={() => confirmDelivery(selectedOrder.order_id, verifyCode)}
                    disabled={verifying || verifyCode.length < 4}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="driver-confirm-btn"
                  >
                    {verifying ? 'Verifying...' : (
                      <><CheckCircle className="h-4 w-4 mr-2" /> Confirm Delivery</>
                    )}
                  </Button>
                </div>
              )}

              {/* Captured delivery proof (after collection) */}
              {selectedOrder.delivery_proof && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-green-600" /> Delivery Proof</p>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedOrder.delivery_proof.photo_path && (
                      <img src={`${API_URL}/api/delivery-proof/${selectedOrder.delivery_proof.photo_path}?token=${token}`} alt="Proof" className="w-full h-24 object-cover rounded border" data-testid="proof-photo-view" />
                    )}
                    {selectedOrder.delivery_proof.signature_path && (
                      <img src={`${API_URL}/api/delivery-proof/${selectedOrder.delivery_proof.signature_path}?token=${token}`} alt="Signature" className="w-full h-24 object-contain bg-white rounded border" data-testid="proof-sig-view" />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Verify Dialog */}
      <Dialog open={!!quickOrder} onOpenChange={(open) => { if (!open) setQuickOrder(null); }}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-600" /> Confirm Delivery
            </DialogTitle>
          </DialogHeader>
          {quickOrder && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600 font-medium">Collection Code</p>
                <p className="font-mono text-2xl font-bold text-green-800 tracking-wider">{quickOrder.collection_code}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-gray-500">Customer</p><p className="font-semibold">{quickOrder.customer_name}</p></div>
                <div><p className="text-xs text-gray-500">Total</p><p className="font-bold text-primary">R{quickOrder.total_amount?.toFixed(0)}</p></div>
              </div>
              <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                {quickOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between px-3 py-2">
                    <span className="text-sm">{item.name} x{item.quantity}</span>
                    <span className="text-sm font-semibold">R{(item.price * item.quantity).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setQuickOrder(null)}>Cancel</Button>
            <Button onClick={() => confirmDelivery(quickOrder.order_id, quickOrder.collection_code)}
              disabled={verifying} className="bg-green-600 hover:bg-green-700" data-testid="driver-quick-confirm">
              {verifying ? 'Verifying...' : 'Confirm & Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
